import { useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Cookie } from 'lucide-react'
import { Button } from '../ui/button'
import { Switch } from '../ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  CONSENT_PURPOSES,
  acceptAll,
  closeConsentPreferences,
  getConsentSnapshot,
  openConsentPreferences,
  rejectAll,
  saveCustomConsent,
  subscribeToConsent,
  type ConsentPurposes,
} from '../../utils/consent'

/**
 * The cookie banner, and the panel behind its "customise" button.
 *
 * The shape is a compliance decision rather than a styling one, so the parts
 * that look like layout are load-bearing:
 *
 * - refusing is one click, on the first layer, in a button that looks like the
 *   accept button — an "accept" that is easier to reach than a "reject" is the
 *   dark pattern the Garante names explicitly;
 * - there is no X and no close-on-outside-click, because dismissing a banner is
 *   not a choice and continuing to scroll is not consent;
 * - the banner does not block the page. Nothing optional runs until it is
 *   answered, so a visitor who ignores it is already being tracked by nothing,
 *   and holding the site hostage for an answer would push them into clicking
 *   whatever makes it go away.
 */
export function ConsentBanner() {
  const state = useSyncExternalStore(subscribeToConsent, getConsentSnapshot)

  if (!state.answered) return <Banner />
  if (state.preferencesOpen) return <PreferencesDialog />
  return null
}

function Banner() {
  const { t } = useTranslation()
  const [customising, setCustomising] = useState(false)

  if (customising) return <PreferencesDialog onClose={() => setCustomising(false)} />

  return (
    <div
      role="region"
      aria-label={t('consent.banner.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-4"
    >
      <div className="container mx-auto flex max-w-4xl flex-col gap-4 md:flex-row md:items-center">
        <Cookie className="hidden h-6 w-6 flex-shrink-0 text-muted-foreground md:block" aria-hidden />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-semibold">{t('consent.banner.title')}</p>
          <p className="text-sm text-muted-foreground">
            {t('consent.banner.description')}{' '}
            <Link to="/cookie-policy" className="underline underline-offset-2 hover:text-foreground">
              {t('consent.links.cookiePolicy')}
            </Link>
            {' · '}
            <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-foreground">
              {t('consent.links.privacyPolicy')}
            </Link>
          </p>
        </div>
        {/* Reject first in the DOM so it is also first for a screen reader and
            for keyboard tabbing, and the same size and weight as accept. */}
        <div className="flex flex-col gap-2 sm:flex-row md:flex-shrink-0">
          <Button variant="outline" onClick={rejectAll} className="sm:min-w-28">
            {t('consent.actions.rejectAll')}
          </Button>
          <Button onClick={acceptAll} className="sm:min-w-28">
            {t('consent.actions.acceptAll')}
          </Button>
          <Button variant="ghost" onClick={() => setCustomising(true)}>
            {t('consent.actions.customise')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PreferencesDialog({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation()
  const state = useSyncExternalStore(subscribeToConsent, getConsentSnapshot)
  const [draft, setDraft] = useState<ConsentPurposes>({ ...state.purposes })

  const close = onClose ?? closeConsentPreferences

  function save() {
    saveCustomConsent(draft)
    onClose?.()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // Closing without saving leaves the earlier answer standing — and where
        // there was none, leaves the banner up. Never an implied acceptance.
        if (!open) close()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('consent.preferences.title')}</DialogTitle>
          <DialogDescription>{t('consent.preferences.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Listed but not switchable: without it the site does not work, so it
              runs on necessity and there is no choice to offer. Shown anyway,
              because the visitor is entitled to know it is there. */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('consent.purposes.necessary.title')}</p>
              <p className="text-xs text-muted-foreground">
                {t('consent.purposes.necessary.description')}
              </p>
            </div>
            <span className="flex-shrink-0 pt-1 text-xs font-medium text-muted-foreground">
              {t('consent.purposes.necessary.always')}
            </span>
          </div>

          {CONSENT_PURPOSES.map((purpose) => (
            <div key={purpose} className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <label htmlFor={`consent-${purpose}`} className="text-sm font-medium">
                  {t(`consent.purposes.${purpose}.title`)}
                </label>
                <p className="text-xs text-muted-foreground">
                  {t(`consent.purposes.${purpose}.description`)}
                </p>
              </div>
              <Switch
                id={`consent-${purpose}`}
                checked={draft[purpose]}
                onCheckedChange={(checked) => setDraft((prev) => ({ ...prev, [purpose]: checked }))}
                className="mt-1 flex-shrink-0"
              />
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          <Link to="/cookie-policy" className="underline underline-offset-2" onClick={() => close()}>
            {t('consent.links.cookiePolicy')}
          </Link>
          {' · '}
          <Link to="/privacy-policy" className="underline underline-offset-2" onClick={() => close()}>
            {t('consent.links.privacyPolicy')}
          </Link>
        </p>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={rejectAll}>
            {t('consent.actions.rejectAll')}
          </Button>
          <Button onClick={save}>{t('consent.actions.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The way back in, for the footer. A choice that cannot be changed is not one. */
export function ConsentPreferencesLink({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <button type="button" onClick={openConsentPreferences} className={className}>
      {t('consent.actions.managePreferences')}
    </button>
  )
}
